import { Timestamp } from 'firebase/firestore';

import { client } from '../axios';
import { Finding, UserFinding } from '../types';

/** A row from `POST /findings/search`. These fields are the whole row: the
 *  endpoint restricts the response to this allowlist, so nothing else arrives
 *  (in particular no `form_answer_values`, which search matches but never
 *  returns). The people, asset and location ids are `keyword`-mapped in the
 *  store, so they arrive as strings. */
export interface FindingStoreRow {
  id: string;
  code?: number | null;
  name: string;
  description: string;
  status: Finding['status'];
  priority: Finding['priority'];
  finding_type: string;
  creation_date: string;
  expiration_date?: string | null;
  creator_id?: string | null;
  creator_name?: string | null;
  assignee_ids?: string[];
  invitee_ids?: string[];
  asset_id?: string | null;
  asset_name?: string | null;
  location_id?: string | null;
  location_type?: Finding['location_type'] | null;
}

/** The keys both endpoints accept. Anything else is dropped server-side.
 *  `counts` takes the same body and ignores `tab`, `ids` and `from`. */
export interface FindingsSearchBody {
  tab?: 'active' | 'closed';
  search_query?: string;
  date_field?: 'creation_date' | 'expiration_date';
  date_from?: string;
  date_to?: string;
  from?: number;
  statuses?: string[];
  priorities?: string[];
  form_ids?: string[];
  assignees?: string[];
  creator_ids?: string[];
  asset_ids?: string[];
  location_ids?: string[];
  ids?: string[];
}

export interface FindingsSearchResponse {
  items: FindingStoreRow[];
  /** `null` on the last page. */
  next_from: number | null;
  /** The page ended at the store's result window, so there are further matches
   *  no paging can reach. `next_from` is `null` here too, which on its own is
   *  indistinguishable from a genuine last page. */
  window_exhausted: boolean;
}

export interface FindingsCountsResponse {
  active: number;
  closed: number;
}

export async function searchFindings(body: FindingsSearchBody): Promise<FindingsSearchResponse> {
  const response = await client.post<FindingsSearchResponse>('/findings/search', body);
  return response.data;
}

export async function countFindings(body: FindingsSearchBody): Promise<FindingsCountsResponse> {
  const response = await client.post<FindingsCountsResponse>('/findings/counts', body);
  return response.data;
}

/** The sidebar badge counts open tickets with no filters of its own, so it is
 *  keyed apart from the list's queries and nothing they invalidate reaches it.
 *  `invalidateFindingsCounts` is what keeps it in step with the tab totals. */
export const FINDINGS_BADGE_QUERY_KEY = ['findingsBadgeCount'];

/** An id the row does not carry must not become `0` or `NaN`: `creator.user_id`
 *  reaches a creator-based visibility check. */
function optionalId(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** `Timestamp.fromDate` accepts an invalid Date and builds a NaN Timestamp from
 *  it, which every consumer then reads as a date. */
function optionalTimestamp(value: string | null | undefined): Timestamp | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : Timestamp.fromDate(date);
}

/** A `Finding` before per-user state reaches it. */
export type StaticFinding = Omit<Finding, 'read' | 'new'>;

function asset(row: FindingStoreRow): StaticFinding['asset'] {
  const id = optionalId(row.asset_id);
  return id !== undefined && row.asset_name ? { id, name: row.asset_name } : undefined;
}

/**
 * Maps a store row onto the `Finding` shape every card, filter and sort already
 * reads, so nothing downstream knows which backend served it.
 *
 * Everything here comes from the row, so it changes only when page data does.
 * A caller re-rendering on per-user state or a local patch reuses the result.
 */
export function adaptStoreRowStatic(row: FindingStoreRow): StaticFinding {
  return {
    id: row.id,
    code: row.code ?? undefined,
    name: row.name,
    description: row.description,
    status: row.status,
    priority: row.priority,
    type: row.finding_type,
    assignees: (row.assignee_ids ?? []).map(Number),
    invitees: (row.invitee_ids ?? []).map(Number),
    creator: { user_id: optionalId(row.creator_id), name: row.creator_name },
    creation_date: optionalTimestamp(row.creation_date),
    expiration_date: optionalTimestamp(row.expiration_date) ?? null,
    // `Number(null)` is 0 and `Number(undefined)` is NaN, and a 0 reads as a
    // real asset downstream, so the id decides whether there is an asset.
    asset: asset(row),
    location: row.location_id ? Number(row.location_id) : undefined,
    location_type: row.location_type ?? undefined,
    deleted: false,
  };
}

/**
 * `read` and `new` never come from the row: they are per-user state and arrive
 * from the Firestore `user_findings` listener. Their defaults match the
 * Firestore path exactly (`FindingsProvider.tsx`): a finding with no per-user
 * document is read and not new, so it neither shows an unread dot nor fires a
 * mark-as-read on first open.
 */
export function adaptStoreRow(
  row: FindingStoreRow,
  perUserState: Record<string, UserFinding>,
): Finding {
  const userState = perUserState?.[row.id];

  return {
    ...adaptStoreRowStatic(row),
    read: userState?.read ?? true,
    new: userState?.new ?? false,
  };
}
